import { useCallback, useContext, useEffect, useState } from 'react';
import { SidebarProvider } from '@/components/ui/sidebar';
import { SidebarContext } from '@/providers/SidebarProvider';
interface ShadcnSidebarAdapterProps {
  children: React.ReactNode;
}

/**
 * Bridges the existing SidebarProvider (body classes, search, responsive)
 * with shadcn's SidebarProvider (UI state for collapse/expand).
 *
 * The existing provider remains the source of truth.
 * At tablet sizes (600-991px), the sidebar auto-collapses to icon-only mode.
 */
export const ShadcnSidebarAdapter = ({
  children
}: ShadcnSidebarAdapterProps) => {
  const {
    isSidebarToggled,
    toggleSidebar
  } = useContext(SidebarContext);

  // The tablet auto-collapse is deferred to a post-mount effect so SSR
  // and the first client render produce identical markup. Reading
  // matchMedia synchronously at render time would flip `open` between
  // server (false) and client (true on tablet), corrupting the
  // hydrated `data-state` and width custom-properties on every tablet
  // visit.
  const [isTablet, setIsTablet] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 991px)');
    const update = () => setIsTablet(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  // At tablet (600-991px), auto-collapse to icon-only mode.
  // At desktop (992px+), respect the user's toggle.
  // Below 600px, the sidebar is hidden entirely via CSS (not rendered as a
  // shadcn Sheet/drawer) — mobile navigation is handled by the TopBar component.
  const open = isTablet ? false : !isSidebarToggled;
  const handleOpenChange = useCallback((newOpen: boolean) => {
    toggleSidebar(!newOpen);
  }, [toggleSidebar]);
  return <SidebarProvider className="h-full w-full" onOpenChange={handleOpenChange} open={open} data-sentry-element="SidebarProvider" data-sentry-component="ShadcnSidebarAdapter" data-sentry-source-file="ShadcnSidebarAdapter.tsx">
      {children}
    </SidebarProvider>;
};